import { useEffect, useState } from 'react';
import { Icon } from '../../components';
import { useTranslation } from '../../providers/UIProvider';
import { ActivityCard } from './ActivityCard';
import { StackedActivities } from './StackedActivities';
import type { NotificationView } from './types';

export interface NotificationCenterProps {
  /** Ordered notifications (most recent first) — typically from `useNotificationsState`. */
  activities: NotificationView[];
  /** Host navigates to the notification's target (uses `targetType` + `targetId`). */
  onOpen: (activity: NotificationView) => void;
}

/**
 * Real-time notification dock floating in the bottom-left corner. Renders nothing when idle; a
 * single pill for one notification; a stacked, expandable deck for several. Purely presentational —
 * data comes from the host (query + subscription), navigation via `onOpen`.
 */
export function NotificationCenter({ activities, onOpen }: NotificationCenterProps) {
  const t = useTranslation('notifications');
  // Ids closed in this dock only — other readers of the same stream still see them.
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set());

  // Forget ids the stream dropped, so a returning id shows again and the set stays small.
  useEffect(() => {
    setDismissed(current => {
      const live = new Set(activities.map(activity => activity.id));
      const kept = [...current].filter(id => live.has(id));
      return kept.length === current.size ? current : new Set(kept);
    });
  }, [activities]);

  const visible = activities.filter(activity => !dismissed.has(activity.id));
  if (visible.length === 0) return null;

  const dismiss = () =>
    setDismissed(current => new Set([...current, ...visible.map(activity => activity.id)]));

  return (
    // Click-through wrapper. On mobile it spans the bottom edge, above the host's floating buttons.
    <div className='pointer-events-none fixed bottom-4 left-4 right-4 z-50 sm:bottom-6 sm:left-6 sm:right-auto sm:z-40 sm:w-80'>
      <div className='pointer-events-auto relative'>
        {visible.length === 1 ? (
          <ActivityCard activity={visible[0]} onOpen={onOpen} />
        ) : (
          <StackedActivities activities={visible} onOpen={onOpen} />
        )}
        <button
          type='button'
          aria-label={t('dismiss')}
          onClick={dismiss}
          className='absolute -right-xs -top-xs flex size-6 items-center justify-center rounded-full bg-background-input text-foreground-muted shadow-md transition-colors hover:bg-background-muted hover:text-foreground-base'
        >
          <Icon name='xmark' size='xs' />
        </button>
      </div>
    </div>
  );
}
